import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft,
  Wallet,
  Plus,
  Minus,
  TrendingUp,
  CreditCard,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WalletManagement() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentType, setAdjustmentType] = useState('add');
  const [adjustmentDescription, setAdjustmentDescription] = useState('');
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  
  const [paymentType, setPaymentType] = useState('full');
  const [paymentPercentage, setPaymentPercentage] = useState(100);
  const [exactAmount, setExactAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Pending month payment states
  const [pendingPaymentType, setPendingPaymentType] = useState('full');
  const [pendingPaymentPercentage, setPendingPaymentPercentage] = useState(100);
  const [pendingExactAmount, setPendingExactAmount] = useState('');
  const [pendingPaymentLoading, setPendingPaymentLoading] = useState(false);

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${userId}`);
      setUser(response.data);
    } catch (error) {
      // Error silenced: 'Failed to fetch user:', error);
      toast.error('Utilizador não encontrado');
      navigate(`/admin/users/${userId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustment = async (e) => {
    e.preventDefault();
    
    if (!adjustmentAmount || parseFloat(adjustmentAmount) <= 0) {
      toast.error('Introduza um montante válido');
      return;
    }

    if (!adjustmentDescription.trim()) {
      toast.error('Introduza uma descrição');
      return;
    }

    setAdjustmentLoading(true);
    try {
      const amount = adjustmentType === 'add' 
        ? parseFloat(adjustmentAmount) 
        : -parseFloat(adjustmentAmount);

      await axios.post(`${API_URL}/admin/wallet/adjust`, {
        user_id: userId,
        amount,
        description: adjustmentDescription
      });

      toast.success('Saldo ajustado com sucesso');
      setAdjustmentAmount('');
      setAdjustmentDescription('');
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao ajustar saldo');
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const handlePayUtility = async () => {
    if (paymentType === 'partial' && (paymentPercentage <= 0 || paymentPercentage > 100)) {
      toast.error('Percentagem inválida');
      return;
    }

    if (paymentType === 'exact') {
      const amount = parseFloat(exactAmount);
      if (!amount || amount <= 0) {
        toast.error('Introduza um montante válido');
        return;
      }
      const monthlyReturn = calculateMonthlyReturn();
      if (amount > monthlyReturn) {
        toast.error(`O montante não pode exceder a utilidade do mês (€${monthlyReturn.toFixed(2)})`);
        return;
      }
    }

    setPaymentLoading(true);
    try {
      const payload = {
        user_id: userId,
        payment_type: paymentType,
        percentage: paymentType === 'partial' ? paymentPercentage : 100
      };
      
      if (paymentType === 'exact') {
        payload.exact_amount = parseFloat(exactAmount);
      }

      const response = await axios.post(`${API_URL}/admin/wallet/pay-utility`, payload);

      const paidAmount = parseFloat(response.data.paid_amount).toFixed(2);
      const reinvestedAmount = parseFloat(response.data.reinvested_amount).toFixed(2);
      
      if (paymentType === 'reinvest') {
        toast.success(`Juros Compostos: €${reinvestedAmount} reinvestido no saldo`);
      } else if (parseFloat(paidAmount) === 0) {
        toast.success(`Reinvestimento total: €${reinvestedAmount} adicionado ao saldo`);
      } else {
        toast.success(`Pagamento processado: €${paidAmount} pago, €${reinvestedAmount} reinvestido`);
      }
      setExactAmount('');
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePayPendingMonth = async (e) => {
    e.preventDefault();
    
    if (!user?.pending_month_return) {
      toast.error('Não há mês pendente de pagamento');
      return;
    }

    if (pendingPaymentType === 'partial' && (pendingPaymentPercentage <= 0 || pendingPaymentPercentage > 100)) {
      toast.error('Percentagem inválida');
      return;
    }

    if (pendingPaymentType === 'exact') {
      const amount = parseFloat(pendingExactAmount);
      if (!amount || amount <= 0) {
        toast.error('Introduza um montante válido');
        return;
      }
      if (amount > user.pending_month_return.amount) {
        toast.error(`O montante não pode exceder o valor pendente (€${user.pending_month_return.amount.toFixed(2)})`);
        return;
      }
    }

    setPendingPaymentLoading(true);
    try {
      const payload = {
        user_id: userId,
        payment_type: pendingPaymentType,
        percentage: pendingPaymentType === 'partial' ? pendingPaymentPercentage : 100
      };
      
      if (pendingPaymentType === 'exact') {
        payload.exact_amount = parseFloat(pendingExactAmount);
      }

      const response = await axios.post(`${API_URL}/admin/wallet/pay-pending-month`, payload);

      const paidAmount = parseFloat(response.data.paid_amount).toFixed(2);
      const reinvestedAmount = parseFloat(response.data.reinvested_amount).toFixed(2);
      const monthName = response.data.month_name;
      
      if (pendingPaymentType === 'reinvest') {
        toast.success(`${monthName}: €${reinvestedAmount} reinvestido no saldo`);
      } else if (parseFloat(paidAmount) === 0) {
        toast.success(`${monthName}: €${reinvestedAmount} adicionado ao saldo`);
      } else {
        toast.success(`${monthName}: €${paidAmount} pago, €${reinvestedAmount} reinvestido`);
      }
      setPendingExactAmount('');
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento pendente');
    } finally {
      setPendingPaymentLoading(false);
    }
  };

  const calculateMonthlyReturn = () => {
    if (!user) return 0;
    // Use the pre-calculated proportional value from backend
    if (user.expected_this_month !== undefined && user.expected_this_month !== null) {
      return Math.round(user.expected_this_month * 100) / 100;
    }
    // Fallback to simple calculation if not available
    const balance = user.balance || 0;
    const profitPercentage = user.profit_percentage || 0;
    return Math.round((balance * profitPercentage / 100) * 100) / 100;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in" data-testid="wallet-management">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(`/admin/users/${userId}`)}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Gestão de Wallet</h1>
          <p className="text-slate-400 text-sm">{user?.name}</p>
        </div>
      </div>

      {/* User Summary */}
      <div className="glass-card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-slate-400 text-sm mb-1">Saldo Atual</p>
            <p className="text-2xl font-bold text-white">{formatCurrency(user?.balance)}</p>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">Taxa de Rendimento</p>
            <p className="text-2xl font-bold text-emerald-400">{user?.profit_percentage}%</p>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Rendimento {user?.current_month_name}
            </p>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(calculateMonthlyReturn())}</p>
            <p className="text-xs text-slate-500">(será pago no próximo mês)</p>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">Rendimentos Acumulados</p>
            <p className="text-2xl font-bold text-purple-400">{formatCurrency(user?.accumulated_returns)}</p>
          </div>
        </div>
        
        {/* Detailed calculation explanation */}
        {user?.monthly_calculation && (
          <div className="mt-6 p-4 bg-slate-800/50 rounded-xl border border-white/10">
            <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              Detalhes do Cálculo - Porque este valor?
            </h3>
            
            <div className="space-y-2 text-sm">
              {/* Base balance return */}
              {user.monthly_calculation.balance_at_start > 0 && (
                <div className="flex justify-between items-center py-1 border-b border-white/5">
                  <span className="text-slate-400">
                    Saldo início do mês ({formatCurrency(user.monthly_calculation.balance_at_start)})
                  </span>
                  <span className="text-emerald-400 font-medium">
                    +{formatCurrency(user.monthly_calculation.base_return)}
                  </span>
                </div>
              )}
              
              {/* New deposits this month */}
              {user.monthly_calculation.deposits_this_month > 0 && (
                <div className="flex justify-between items-center py-1 border-b border-white/5">
                  <span className="text-slate-400">
                    Novos depósitos ({formatCurrency(user.monthly_calculation.deposits_this_month)}) - proporcional
                  </span>
                  <span className="text-blue-400 font-medium">
                    +{formatCurrency(user.monthly_calculation.deposits_return)}
                  </span>
                </div>
              )}
              
              {/* Withdrawals this month - show reduction */}
              {user.monthly_calculation.withdrawals_this_month > 0 && (
                <div className="flex justify-between items-center py-1 border-b border-white/5">
                  <span className="text-slate-400">
                    Levantamentos ({formatCurrency(user.monthly_calculation.withdrawals_this_month)}) - redução
                  </span>
                  <span className="text-red-400 font-medium">
                    -{formatCurrency(user.monthly_calculation.withdrawals_reduction || 0)}
                  </span>
                </div>
              )}
              
              {/* Total */}
              <div className="flex justify-between items-center pt-2 mt-2 border-t border-white/20">
                <span className="text-white font-semibold">Total a Pagar Este Mês</span>
                <span className="text-emerald-400 font-bold text-lg">
                  {formatCurrency(user.monthly_calculation.total_expected)}
                </span>
              </div>
            </div>
            
            <p className="text-xs text-slate-500 mt-3">
              * Saldo existente recebe rendimento completo ({user?.profit_percentage}%). Depósitos/levantamentos recebem ajuste proporcional aos dias restantes do mês.
            </p>
          </div>
        )}
      </div>

      {/* Pending Month Return Alert */}
      {user?.pending_month_return && (
        <div className="glass-card p-6 mb-6 border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-amber-400 mb-1">
                ⚠️ Mês Anterior Pendente de Pagamento
              </h3>
              <p className="text-white text-xl font-bold mb-2">
                {user.pending_month_return.month_name}: {formatCurrency(user.pending_month_return.amount)}
              </p>
              <p className="text-slate-400 text-sm mb-4">
                Este valor corresponde ao rendimento de {user.pending_month_return.month_name} que não foi pago no período correto.
              </p>

              {/* Payment options for pending month */}
              <form onSubmit={handlePayPendingMonth} className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setPendingPaymentType('full')}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      pendingPaymentType === 'full'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20'
                    }`}
                  >
                    Pagar Total
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingPaymentType('reinvest')}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      pendingPaymentType === 'reinvest'
                        ? 'bg-blue-500 text-white'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20'
                    }`}
                  >
                    Reinvestir Tudo
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingPaymentType('partial')}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      pendingPaymentType === 'partial'
                        ? 'bg-purple-500 text-white'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20'
                    }`}
                  >
                    Parcial (%)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingPaymentType('exact')}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      pendingPaymentType === 'exact'
                        ? 'bg-orange-500 text-white'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20'
                    }`}
                  >
                    Valor Exato
                  </button>
                </div>

                {pendingPaymentType === 'partial' && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-sm">Pagar</span>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={pendingPaymentPercentage}
                      onChange={(e) => setPendingPaymentPercentage(parseInt(e.target.value) || 0)}
                      className="w-20 bg-[#161C22] border-white/10 text-white rounded-lg"
                    />
                    <span className="text-slate-400 text-sm">% (reinvestir restante)</span>
                  </div>
                )}

                {pendingPaymentType === 'exact' && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-sm">Pagar €</span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={pendingExactAmount}
                      onChange={(e) => {
                        const value = e.target.value.replace(',', '.');
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setPendingExactAmount(value);
                        }
                      }}
                      placeholder="0.00"
                      className="w-32 bg-[#161C22] border-white/10 text-white rounded-lg"
                    />
                    <span className="text-slate-400 text-sm">(reinvestir restante)</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={pendingPaymentLoading}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl h-11 w-full sm:w-auto"
                  data-testid="pay-pending-month-btn"
                >
                  {pendingPaymentLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Pagar {user.pending_month_return.month_name}
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Manual Adjustment */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-400" />
            Ajuste Manual de Saldo
          </h2>
          
          <form onSubmit={handleAdjustment} className="space-y-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setAdjustmentType('add')}
                className={`flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                  adjustmentType === 'add'
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                    : 'border-white/10 text-slate-400 hover:border-white/20'
                }`}
              >
                <Plus className="w-5 h-5" />
                Adicionar
              </button>
              <button
                type="button"
                onClick={() => setAdjustmentType('subtract')}
                className={`flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                  adjustmentType === 'subtract'
                    ? 'border-red-500 bg-red-500/10 text-red-400'
                    : 'border-white/10 text-slate-400 hover:border-white/20'
                }`}
              >
                <Minus className="w-5 h-5" />
                Subtrair
              </button>
            </div>

            <div>
              <Label className="text-slate-300">Montante (€)</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={adjustmentAmount}
                onChange={(e) => {
                  // Allow both comma and dot as decimal separator
                  const value = e.target.value.replace(',', '.');
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setAdjustmentAmount(value);
                  }
                }}
                placeholder="0.00"
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                required
                data-testid="adjustment-amount"
              />
            </div>

            <div>
              <Label className="text-slate-300">Descrição</Label>
              <Input
                value={adjustmentDescription}
                onChange={(e) => setAdjustmentDescription(e.target.value)}
                placeholder="Motivo do ajuste"
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                required
                data-testid="adjustment-description"
              />
            </div>

            <Button
              type="submit"
              disabled={adjustmentLoading}
              className={`w-full rounded-xl ${
                adjustmentType === 'add'
                  ? 'bg-emerald-500 hover:bg-emerald-600'
                  : 'bg-red-500 hover:bg-red-600'
              }`}
              data-testid="submit-adjustment"
            >
              {adjustmentLoading ? 'A processar...' : 'Confirmar Ajuste'}
            </Button>
          </form>
        </div>

        {/* Utility Payment */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Pagamento de Utilidades
          </h2>

          <div className="space-y-4">
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
              <p className="text-slate-400 text-sm mb-1">Rendimento proporcional deste mês</p>
              <p className="text-2xl font-bold text-emerald-400">{formatCurrency(calculateMonthlyReturn())}</p>
              <p className="text-xs text-slate-500 mt-1">
                {user?.days_invested || 0} de {user?.days_in_month || 30} dias • {user?.profit_percentage}% mensal
              </p>
            </div>

            <div>
              <Label className="text-slate-300">Tipo de Pagamento</Label>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl" data-testid="payment-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#252F38] border-white/10">
                  <SelectItem value="full">Pagamento Completo (100%)</SelectItem>
                  <SelectItem value="partial">Pagamento Parcial (%)</SelectItem>
                  <SelectItem value="exact">Valor Exacto (€)</SelectItem>
                  <SelectItem value="reinvest">Reinvestimento Total (Juros Compostos)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {paymentType === 'reinvest' && (
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">Juros Compostos</span>
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  O rendimento de <span className="text-white font-semibold">{formatCurrency(calculateMonthlyReturn())}</span> será 
                  adicionado ao saldo para gerar mais rendimentos no próximo mês.
                </p>
                <div className="p-3 bg-white/5 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Saldo atual:</span>
                    <span className="text-white">{formatCurrency(user?.balance || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-400">Rendimento a acumular:</span>
                    <span className="text-emerald-400">+{formatCurrency(calculateMonthlyReturn())}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2 pt-2 border-t border-white/10">
                    <span className="text-slate-400">Novo saldo:</span>
                    <span className="text-white font-semibold">{formatCurrency((user?.balance || 0) + calculateMonthlyReturn())}</span>
                  </div>
                </div>
              </div>
            )}

            {paymentType === 'partial' && (
              <div>
                <Label className="text-slate-300">Percentagem a Pagar (%)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={paymentPercentage}
                  onChange={(e) => setPaymentPercentage(parseInt(e.target.value))}
                  className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                  data-testid="payment-percentage"
                />
                <div className="mt-2 p-3 bg-white/5 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Valor a pagar:</span>
                    <span className="text-white">{formatCurrency(calculateMonthlyReturn() * paymentPercentage / 100)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-400">Valor a reinvestir:</span>
                    <span className="text-blue-400">{formatCurrency(calculateMonthlyReturn() * (100 - paymentPercentage) / 100)}</span>
                  </div>
                </div>
              </div>
            )}

            {paymentType === 'exact' && (
              <div>
                <Label className="text-slate-300">Valor Exacto a Pagar (€)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={exactAmount}
                  onChange={(e) => {
                    // Allow both comma and dot as decimal separator
                    const value = e.target.value.replace(',', '.');
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setExactAmount(value);
                    }
                  }}
                  placeholder="0.00"
                  className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                  data-testid="exact-amount"
                />
                <div className="mt-2 p-3 bg-white/5 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Valor a pagar:</span>
                    <span className="text-white">{formatCurrency(parseFloat(exactAmount || 0))}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-400">Valor a reinvestir:</span>
                    <span className="text-blue-400">{formatCurrency(calculateMonthlyReturn() - parseFloat(exactAmount || 0))}</span>
                  </div>
                  <p className="text-xs text-amber-400 mt-2">
                    Máximo: {formatCurrency(calculateMonthlyReturn())}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
              <RefreshCw className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-blue-400 font-medium">Reinvestimento Automático</p>
                <p className="text-slate-400">A parte não retirada será adicionada ao saldo, gerando juros compostos.</p>
              </div>
            </div>

            <Button
              onClick={handlePayUtility}
              disabled={paymentLoading || !user?.balance}
              className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
              data-testid="submit-payment"
            >
              {paymentLoading ? 'A processar...' : 'Processar Pagamento'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
